import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { FinanceTransaction } from '@/types/finance';

const NAVY: [number, number, number] = [17, 53, 84];
const INCOME: [number, number, number] = [22, 163, 74];
const EXPENSE: [number, number, number] = [220, 38, 38];

const fmtBrl = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

const fmtBrlPlain = (n: number) => n.toFixed(2).replace('.', ',');

interface ExportRow extends FinanceTransaction {
  accountName: string;
  categoryName: string;
  costCenterName?: string;
  counterpartyName?: string;
}

export interface ExportTransactionsInput {
  title: string;
  subtitle?: string;
  clientName: string;
  rows: ExportRow[];
  /** Texto opcional resumindo os filtros aplicados (mostrado no header). */
  filtersSummary?: string;
}

function totalOf(r: ExportRow): number {
  return Number(r.amount) + Number(r.interest ?? 0) + Number(r.fine ?? 0);
}

function statusLabel(t: FinanceTransaction): string {
  if (t.status === 'completed') return 'Pago';
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return t.dueDate < startOfToday ? 'Atrasado' : 'A vencer';
}

export function exportTransactionsPdf(input: ExportTransactionsInput): void {
  const { title, subtitle, clientName, rows, filtersSummary } = input;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;

  const totalIncome = rows
    .filter((r) => r.type === 'income')
    .reduce((s, r) => s + totalOf(r), 0);
  const totalExpense = rows
    .filter((r) => r.type === 'expense')
    .reduce((s, r) => s + totalOf(r), 0);

  // Header
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageWidth, 90, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(title, margin, 40);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(clientName, margin, 60);
  if (subtitle) {
    doc.setFontSize(9);
    doc.text(subtitle, margin, 75);
  }

  doc.setFontSize(9);
  doc.text(
    `Gerado em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`,
    pageWidth - margin,
    60,
    { align: 'right' },
  );

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  let cursorY = 110;

  if (filtersSummary) {
    doc.setFont('helvetica', 'normal');
    doc.text(filtersSummary, margin, cursorY);
    cursorY += 16;
  }

  // Resumo
  doc.setFillColor(245, 247, 250);
  doc.rect(margin, cursorY, pageWidth - 2 * margin, 56, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text('Lançamentos', margin + 12, cursorY + 18);
  doc.text('Receitas', margin + 180, cursorY + 18);
  doc.text('Despesas', margin + 320, cursorY + 18);
  doc.text('Saldo', margin + 460, cursorY + 18);

  doc.setFontSize(13);
  doc.setTextColor(20, 20, 20);
  doc.text(String(rows.length), margin + 12, cursorY + 42);

  doc.setTextColor(...INCOME);
  doc.text(fmtBrl(totalIncome), margin + 180, cursorY + 42);

  doc.setTextColor(...EXPENSE);
  doc.text(fmtBrl(totalExpense), margin + 320, cursorY + 42);

  const balance = totalIncome - totalExpense;
  doc.setTextColor(balance >= 0 ? INCOME[0] : EXPENSE[0], balance >= 0 ? INCOME[1] : EXPENSE[1], balance >= 0 ? INCOME[2] : EXPENSE[2]);
  doc.text(fmtBrl(balance), margin + 460, cursorY + 42);

  cursorY += 76;

  autoTable(doc, {
    startY: cursorY,
    margin: { left: margin, right: margin },
    head: [['Data', 'Descrição', 'Tipo', 'Conta', 'Categoria', 'Status', 'Valor']],
    body: rows.map((r) => {
      const refDate = r.paymentDate ?? r.dueDate;
      return [
        format(refDate, 'dd/MM/yyyy', { locale: ptBR }),
        r.description,
        r.type === 'income' ? 'Receita' : r.type === 'expense' ? 'Despesa' : 'Transferência',
        r.accountName,
        r.categoryName,
        statusLabel(r),
        fmtBrl(totalOf(r)),
      ];
    }),
    headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 60 },
      6: { halign: 'right', fontStyle: 'bold' },
    },
    didDrawPage: () => {
      const pageCount = doc.getNumberOfPages();
      const currentPage = doc.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `Página ${currentPage} de ${pageCount}`,
        pageWidth - margin,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'right' },
      );
    },
  });

  const safeTitle = title
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, '-');
  doc.save(`transacoes-${safeTitle}-${format(new Date(), 'yyyyMMdd-HHmm')}.pdf`);
}

export function exportTransactionsCsv(input: ExportTransactionsInput): void {
  const { title, rows } = input;
  const csvRows = rows.map((r) => {
    const refDate = r.paymentDate ?? r.dueDate;
    return {
      Data: format(refDate, 'dd/MM/yyyy', { locale: ptBR }),
      Tipo: r.type === 'income' ? 'Receita' : r.type === 'expense' ? 'Despesa' : 'Transferência',
      Descrição: r.description,
      Contraparte: r.counterpartyName ?? '',
      Conta: r.accountName,
      Categoria: r.categoryName,
      'Centro de Custo': r.costCenterName ?? '',
      Status: statusLabel(r),
      Principal: fmtBrlPlain(Number(r.amount)),
      Multa: fmtBrlPlain(Number(r.fine ?? 0)),
      Juros: fmtBrlPlain(Number(r.interest ?? 0)),
      Valor: fmtBrlPlain(totalOf(r)),
    };
  });

  const csv = Papa.unparse(csvRows, { delimiter: ';' });
  const bom = '﻿';
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeTitle = title
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, '-');
  a.download = `transacoes-${safeTitle}-${format(new Date(), 'yyyyMMdd-HHmm')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
